import React, { useMemo, useState } from 'react';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { useTasks } from '../../hooks/useTasks';
import TaskView from '../tasks/TaskView';
import TaskForm from '../tasks/TaskForm';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface ProjectBacklogViewProps {
  project: Project;
}

const ProjectBacklogView: React.FC<ProjectBacklogViewProps> = ({ project }) => {
  const { tasks, loading, updateTask } = useTasks();
  const [expandedSprints, setExpandedSprints] = useState<Set<string>>(new Set(['backlog']));
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Projekt feladatainak szűrése
  const projectTasks = useMemo(() => {
    return tasks.filter(task => task.projectId === project.id);
  }, [tasks, project.id]);

  // Feladatok csoportosítása prioritás szerint
  const tasksByPriority = useMemo(() => {
    const groups = {
      urgent: projectTasks.filter(t => t.priority === 'urgent'),
      high: projectTasks.filter(t => t.priority === 'high'),
      medium: projectTasks.filter(t => t.priority === 'medium'),
      low: projectTasks.filter(t => t.priority === 'low')
    };
    return groups;
  }, [projectTasks]);

  // Toggle sprint
  const toggleSprint = (sprintId: string) => {
    setExpandedSprints(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sprintId)) {
        newSet.delete(sprintId);
      } else {
        newSet.add(sprintId);
      }
      return newSet;
    });
  };

  // Prioritás szín
  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      urgent: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
      high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
      medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
      low: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
    };
    return colors[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
  };

  // Státusz szín
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      todo: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      in_progress: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Backlog Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {project.name} - Backlog
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {projectTasks.length} feladat összesen - Priorizálás és sprint tervezés
        </p>
      </div>

      {/* Priority Groups */}
      <div className="space-y-4">
        {/* Urgent */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => toggleSprint('urgent')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSprints.has('urgent') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">🔥 Sürgős</span>
              </div>
            </div>
            <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-3 py-1 rounded-full text-sm font-medium">
              {tasksByPriority.urgent.length}
            </span>
          </button>

          {expandedSprints.has('urgent') && (
            <div className="px-6 pb-4 space-y-2">
              {tasksByPriority.urgent.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                  Nincs sürgős feladat
                </div>
              ) : (
                tasksByPriority.urgent.map(task => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {task.description}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          {task.taskNumber && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {task.taskNumber}
                            </span>
                          )}
                        </div>
                      </div>
                      {task.assignedToName && (
                        <div className="ml-4">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                              {task.assignedToName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* High */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => toggleSprint('high')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSprints.has('high') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">Magas prioritás</span>
              </div>
            </div>
            <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-3 py-1 rounded-full text-sm font-medium">
              {tasksByPriority.high.length}
            </span>
          </button>

          {expandedSprints.has('high') && (
            <div className="px-6 pb-4 space-y-2">
              {tasksByPriority.high.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                  Nincs magas prioritású feladat
                </div>
              ) : (
                tasksByPriority.high.map(task => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-orange-300 dark:hover:border-orange-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {task.description}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          {task.taskNumber && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {task.taskNumber}
                            </span>
                          )}
                        </div>
                      </div>
                      {task.assignedToName && (
                        <div className="ml-4">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                              {task.assignedToName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Medium */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => toggleSprint('medium')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSprints.has('medium') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">Közepes prioritás</span>
              </div>
            </div>
            <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-3 py-1 rounded-full text-sm font-medium">
              {tasksByPriority.medium.length}
            </span>
          </button>

          {expandedSprints.has('medium') && (
            <div className="px-6 pb-4 space-y-2">
              {tasksByPriority.medium.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                  Nincs közepes prioritású feladat
                </div>
              ) : (
                tasksByPriority.medium.map(task => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-yellow-300 dark:hover:border-yellow-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {task.description}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          {task.taskNumber && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {task.taskNumber}
                            </span>
                          )}
                        </div>
                      </div>
                      {task.assignedToName && (
                        <div className="ml-4">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                              {task.assignedToName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Low */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => toggleSprint('low')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSprints.has('low') ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">Alacsony prioritás</span>
              </div>
            </div>
            <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-sm font-medium">
              {tasksByPriority.low.length}
            </span>
          </button>

          {expandedSprints.has('low') && (
            <div className="px-6 pb-4 space-y-2">
              {tasksByPriority.low.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                  Nincs alacsony prioritású feladat
                </div>
              ) : (
                tasksByPriority.low.map(task => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {task.description}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          {task.taskNumber && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {task.taskNumber}
                            </span>
                          )}
                        </div>
                      </div>
                      {task.assignedToName && (
                        <div className="ml-4">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                              {task.assignedToName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && !editingTask && (
        <TaskView
          task={selectedTask}
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onEdit={(task) => setEditingTask(task)}
        />
      )}

      {/* Task Edit Form */}
      {editingTask && (
        <TaskForm
          initialData={editingTask}
          isOpen={!!editingTask}
          onClose={() => {
            setEditingTask(null);
            setSelectedTask(null);
          }}
          onSubmit={async (taskData) => {
            try {
              await updateTask(editingTask.id, taskData);
              setEditingTask(null);
              setSelectedTask(null);
            } catch (error) {
              console.error('Error updating task:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectBacklogView;
